import {
  Decimal,
  type BreBAliasType,
  type DemoPaymentScenario,
  type PaymentIntent,
} from '@paguz/contracts';
import { useCallback, useEffect, useState } from 'react';
import { newIdempotencyKey } from '../../api/sca.ts';

/** Comercio resuelto (antes de pedir el monto: protección contra fraude). */
export interface ResolvedPayee {
  aliasType: BreBAliasType;
  aliasValue: string;
  name: string;
  participantName: string;
  maskedAccount?: string | undefined;
  city?: string | undefined;
  nickname?: string | null | undefined;
}

export interface CheckoutDraft {
  source: 'QR' | 'KEY' | 'FREQUENT' | null;
  payee: ResolvedPayee | null;
  qrPayload: string | null;
  qrType: 'STATIC' | 'DYNAMIC' | null;
  /** Monto fijado por un QR dinámico (no editable). */
  lockedAmount: string | null;
  reference: string | null;
  /** Monto canónico de `AmountInput` (`"180000"`), `""` si vacío. */
  amount: string;
  description: string;
  installments: number | null;
  /** Solo demo: resultado de Bre-B elegido por el presentador. */
  demoScenario: DemoPaymentScenario;
  /** Llaves de idempotencia: la de creación se renueva si cambian comercio, monto o cuotas. */
  createKey: string;
  createFingerprint: string;
  confirmKey: string;
  intent: Pick<PaymentIntent, 'id' | 'scaChallengeId' | 'simulation' | 'status'> | null;
}

export type CheckoutStep = 'origen' | 'monto' | 'cuotas' | 'confirmar';
export const CHECKOUT_STEPS: readonly CheckoutStep[] = ['origen', 'monto', 'cuotas', 'confirmar'];

const KEY = 'paguz.web.checkout.v1';

export function emptyDraft(): CheckoutDraft {
  return {
    source: null,
    payee: null,
    qrPayload: null,
    qrType: null,
    lockedAmount: null,
    reference: null,
    amount: '',
    description: '',
    installments: null,
    demoScenario: 'NORMAL',
    createKey: newIdempotencyKey(),
    createFingerprint: '',
    confirmKey: newIdempotencyKey(),
    intent: null,
  };
}

function load(): CheckoutDraft {
  try {
    const raw = globalThis.sessionStorage.getItem(KEY);
    if (raw !== null) return { ...emptyDraft(), ...(JSON.parse(raw) as Partial<CheckoutDraft>) };
  } catch {
    // borrador corrupto o sin almacenamiento
  }
  return emptyDraft();
}

export function clearStoredDraft(): void {
  try {
    globalThis.sessionStorage.removeItem(KEY);
  } catch {
    // nada
  }
}

/** Monto para la API: string decimal con 2 decimales. */
export function toApiAmount(amount: string): string {
  return new Decimal(amount === '' ? '0' : amount).toFixed(2);
}

/** Monto efectivo (el del QR dinámico manda). */
export function effectiveAmount(draft: CheckoutDraft): string {
  return draft.lockedAmount ?? draft.amount;
}

export function fingerprint(draft: CheckoutDraft): string {
  return [
    draft.payee?.aliasType,
    draft.payee?.aliasValue,
    draft.qrPayload,
    effectiveAmount(draft),
    draft.installments,
    draft.description,
    draft.demoScenario,
  ].join('|');
}

/** El paso más avanzado al que se puede llegar con los datos del borrador. */
export function maxReachableStep(draft: CheckoutDraft): CheckoutStep {
  if (draft.payee === null) return 'origen';
  const amount = effectiveAmount(draft);
  if (amount === '' || new Decimal(amount).lte(0)) return 'monto';
  if (draft.installments === null) return 'cuotas';
  return 'confirmar';
}

/**
 * Borrador del checkout en `sessionStorage`: sobrevive a recargas y a la navegación atrás/adelante
 * entre pasos, y conserva las llaves de idempotencia (reintentar no duplica el pago).
 */
export function useCheckoutDraft() {
  const [draft, setDraft] = useState<CheckoutDraft>(load);

  useEffect(() => {
    try {
      globalThis.sessionStorage.setItem(KEY, JSON.stringify(draft));
    } catch {
      // sin almacenamiento: el borrador vive en memoria
    }
  }, [draft]);

  const update = useCallback((patch: Partial<CheckoutDraft>) => {
    setDraft((prev) => {
      const next = { ...prev, ...patch };
      // Cambió lo que se paga: la intención anterior ya no sirve.
      if (prev.intent !== null && fingerprint(next) !== fingerprint(prev) && !('intent' in patch)) {
        next.intent = null;
      }
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    clearStoredDraft();
    setDraft(emptyDraft());
  }, []);

  return { draft, update, reset };
}

/** Llave de creación vigente para el contenido actual del borrador. */
export function createKeyFor(draft: CheckoutDraft): { key: string; fingerprint: string } {
  const current = fingerprint(draft);
  return current === draft.createFingerprint
    ? { key: draft.createKey, fingerprint: current }
    : { key: newIdempotencyKey(), fingerprint: current };
}
